/**
 * Exemplar Hooks
 * TanStack Query hooks for exemplar management in the AI Sandbox
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { exemplarRepo } from '@/lib/repositories'
import type {
  CreateExemplarData,
  UpdateExemplarData,
  ExemplarCategory,
} from '@/lib/repositories'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

// Query keys
export const EXEMPLARS_KEY = ['exemplars'] as const

/**
 * Fetch paginated exemplars
 */
export function useExemplars(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: [...EXEMPLARS_KEY, page, pageSize],
    queryFn: () => exemplarRepo.listExemplars({ page, pageSize }),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Fetch exemplars with auto-polling while any exemplar is processing
 */
export function useExemplarsWithPolling(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: [...EXEMPLARS_KEY, page, pageSize],
    queryFn: () => exemplarRepo.listExemplars({ page, pageSize }),
    staleTime: 10_000,
    placeholderData: (previousData) => previousData,
    refetchInterval: (query) => {
      const result = query.state.data
      const hasProcessing = result?.data?.some(
        (e) => e.status === 'processing' || e.status === 'pending'
      )
      return hasProcessing ? 3000 : false
    },
  })
}

/**
 * Create an exemplar
 */
export function useCreateExemplar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateExemplarData) =>
      exemplarRepo.createExemplar(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: EXEMPLARS_KEY })
      analytics.capture(AnalyticsEvents.ADMIN_EXEMPLAR_CREATED, { category: data.category })
    },
  })
}

/**
 * Update an exemplar
 */
export function useUpdateExemplar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateExemplarData }) =>
      exemplarRepo.updateExemplar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXEMPLARS_KEY })
    },
  })
}

/**
 * Delete an exemplar
 */
export function useDeleteExemplar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => exemplarRepo.deleteExemplar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXEMPLARS_KEY })
      analytics.capture(AnalyticsEvents.ADMIN_EXEMPLAR_DELETED)
    },
  })
}

/**
 * Toggle exemplar active/inactive
 */
export function useToggleExemplar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      exemplarRepo.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXEMPLARS_KEY })
    },
  })
}

/**
 * Test retrieval — find which exemplars match a given query
 */
export function useTestRetrieval() {
  return useMutation({
    mutationFn: (query: string) => exemplarRepo.testRetrieval(query),
  })
}

/**
 * Bulk import exemplars from structured data (CSV)
 */
export function useBulkImportExemplars() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (
      exemplars: Array<{
        question: string
        answer: string
        category: ExemplarCategory
        tags?: string[]
      }>
    ) => exemplarRepo.bulkImportExemplars(exemplars),
    onSuccess: (_, exemplars) => {
      queryClient.invalidateQueries({ queryKey: EXEMPLARS_KEY })
      analytics.capture(AnalyticsEvents.ADMIN_EXEMPLAR_BULK_IMPORTED, {
        count: exemplars.length,
        source: 'csv',
      })
    },
  })
}

/**
 * Bulk import exemplars from a PDF file
 */
export function useBulkImportFromPdf() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      file,
      defaultCategory,
    }: {
      file: File
      defaultCategory: ExemplarCategory
    }) => exemplarRepo.bulkImportFromPdf(file, defaultCategory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXEMPLARS_KEY })
      analytics.capture(AnalyticsEvents.ADMIN_EXEMPLAR_BULK_IMPORTED, { source: 'pdf' })
    },
  })
}

/**
 * Delete all exemplars from a bulk import batch
 */
export function useDeleteBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (batchId: string) => exemplarRepo.deleteBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXEMPLARS_KEY })
    },
  })
}

